/**
 * Operator settings: the shapes the settings page and the Worker agree on.
 *
 * Types and two small constants only — this file ships to the browser through
 * the barrel. The registry (which settings exist, their schemas and defaults)
 * lives in the Worker at `apps/api/src/settings/registry.ts`; the page renders
 * whatever `GET /admin/settings` describes and never has its own list.
 */

export const SETTING_GROUPS = [
  'general',
  'signup',
  'email',
  'telegram',
  'payouts',
  'referrals',
  'review',
  'integrations',
] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];

export const SETTING_GROUP_LABELS: Readonly<Record<SettingGroup, string>> = Object.freeze({
  general: 'General',
  signup: 'Signup & approval',
  email: 'Email',
  telegram: 'Telegram',
  payouts: 'Payouts',
  referrals: 'Referrals',
  review: 'Review',
  integrations: 'Integrations',
});

/** How the page draws the field. */
export type SettingKind =
  | 'text'
  | 'url'
  | 'email'
  | 'boolean'
  | 'integer'
  | 'money'
  | 'select'
  | 'timezone'
  | 'secret';

/** Where the effective value came from. */
export type SettingSource = 'settings' | 'deploy' | 'default';

export interface SettingSecretState {
  /** Somebody has set it — from the page or in the deploy. */
  set: boolean;
  /** The tail the page may show (`…ab12`), or null when none is offered. */
  hint: string | null;
  /** Set on the page but the Worker cannot open it (no or changed TOKEN_KEK). */
  unreadable: boolean;
}

export interface SettingView {
  key: string;
  group: SettingGroup;
  label: string;
  help: string;
  kind: SettingKind;
  secret: boolean;
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
  /** The effective value. Always null for a secret. */
  value: string | number | boolean | null;
  source: SettingSource;
  /** The registry default. Null for a secret. */
  default: string | number | boolean | null;
  /** What the deploy provides, for the chip's tooltip. Null for a secret or when none. */
  deployValue: string | null;
  /** Whether the deploy provides one at all (secrets included). */
  deploySet: boolean;
  secretState: SettingSecretState | null;
  updatedAt: string | null;
  updatedByEmail: string | null;
}

export interface SettingsChange {
  id: string;
  key: string;
  before: unknown;
  after: unknown;
  actorEmail: string | null;
  createdAt: string;
}

export interface AdminSettingsResponse {
  settings: SettingView[];
  /** The keys that seal the store: shown with their status, never editable. */
  deployOnly: Array<{ key: string; label: string; help: string; set: boolean }>;
  /** Whether TOKEN_KEK is usable — without it no secret can be saved or read. */
  kekConfigured: boolean;
  /** The operator's own address, where a test email goes. */
  operatorEmail: string;
  recentChanges: SettingsChange[];
}

/** `{ KEY: value }`; `null` clears the row so the deploy value or default applies again. */
export type SettingsPatchBody = Record<string, string | number | boolean | null>;

export interface SettingsTestResult {
  ok: boolean;
  message: string;
  detail?: Record<string, unknown>;
}

/** A DNS record Resend expects on the sending domain. */
export interface ResendDnsRecord {
  type: string;
  name: string;
  value: string;
  priority?: number | null;
  status?: string | null;
}

export interface ResendDomainResult extends SettingsTestResult {
  domain: string | null;
  status: string | null;
  records: ResendDnsRecord[] | null;
}

/** What a signed-in creator may know about the operator's configuration. */
export interface PublicConfigResponse {
  reviewSlaHours: number;
  referral: { rateBasisPoints: number; capCents: number };
  /** Where payouts come from, so a creator recognises the payment. */
  paidFrom: { paypalEmail: string | null };
}
